import type { Token } from '@polaris/core/models'

/**
 * Configuration map for chain-specific fee tokens.
 * Add new chains here as needed without modifying the function logic.
 *
 * Format: { chainIdentifier: ticker }
 */
const CHAIN_FEE_TOKENS: Record<string, string> = {
  // Cosmos chains
  osmosis: 'OSMO',
  cosmoshub: 'ATOM',
  celestia: 'TIA',
  injective: 'INJ',
  dydx: 'DYDX',
} as const

/**
 * Special denom values that indicate native fee tokens
 */
const NATIVE_DENOMS = ['evm-native', 'solana-native', 'utxo-native'] as const

/**
 * Determines if a token is a native fee/gas token for its chain.
 *
 * Native tokens are used to pay transaction fees on their respective chains.
 * Examples: ETH on Ethereum, SOL on Solana, OSMO on Osmosis, BTC on Bitcoin.
 *
 * This uses the token's `kind` field from backend metadata as the primary check,
 * with configurable fallbacks for edge cases where fee data isn't loaded yet.
 *
 * @param token - The token to check
 * @returns true if the token is a native fee token, false otherwise
 */
export function isFeeToken(
  token: Pick<Token, 'kind' | 'denom' | 'ticker' | 'chain_id'>,
): boolean {
  // Primary check: Backend metadata
  if (token.kind === 'native') {
    return true
  }

  // Check special native denoms
  if (NATIVE_DENOMS.includes(token.denom as (typeof NATIVE_DENOMS)[number])) {
    return true
  }

  // Fallback: Check against configured chain fee tokens
  if (token.ticker && token.chain_id) {
    for (const [chainIdentifier, expectedTicker] of Object.entries(
      CHAIN_FEE_TOKENS,
    )) {
      if (
        token.chain_id.includes(chainIdentifier) &&
        token.ticker === expectedTicker
      ) {
        return true
      }
    }
  }

  return false
}
